import { Button } from '@capturi/ui-components'
import { useModal } from '@capturi/use-modal'
import {
  Box,
  FormLabel,
  HStack,
  IconButton,
  Select,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react'
import { i18n } from '@lingui/core'
import { Trans, t } from '@lingui/macro'
import { FC } from 'react'
import { MdDelete } from 'react-icons/md'
import { CopilotConfig } from '../api/types'
import { ConfirmDeleteTokenModal } from './ConfirmDeleteTokenModal'
import { TokenModal } from './TokenModal'

type Props = {
  copilotConfig: CopilotConfig
  onLanguageChange: (value: string) => void
  onProviderChange: (value: string) => void
  onModuleChange: (key: string, value: boolean) => void
}

const languages: string[] = [
  'da-DK',
  'en-US',
  'de-DE',
  'nb-NO',
  'sv-SE',
  'fi-FI',
  'nl-NL',
  'pl-PL',
  'es-ES',
  'pt-PT',
]
const modulePropWithTitle = (
  module: CopilotConfig['modules'],
): { [key: string]: { title: string; value: boolean } } => {
  return {
    handover: {
      title: t`Handover`,
      value: module.handover,
    },
    previousInteractions: {
      title: t`Previous Interactions`,
      value: module.previousInteractions,
    },
    liveSummary: {
      title: t`Live Summary`,
      value: module.liveSummary,
    },
    askCopilot: {
      title: t`Ask Copilot`,
      value: module.askCopilot,
    },
  }
}

export const BaseConfig: FC<Props> = ({
  copilotConfig,
  onLanguageChange,
  onProviderChange,
  onModuleChange,
}) => {
  const [openTokenModal] = useModal(TokenModal)
  const [openDeleteTokenModal] = useModal(ConfirmDeleteTokenModal)

  return (
    <VStack spacing={4} align="stretch" w="50%">
      <Text color="greys.400">
        <Trans>Select your desired live summary language</Trans>
      </Text>
      <Select
        id="conversation-language"
        onChange={(e) => onLanguageChange(e.target.value)}
        value={copilotConfig.language}
      >
        {languages.map((l) => (
          <option value={l} key={l}>
            {l}
          </option>
        ))}
      </Select>
      <Text color="greys.400">
        <Trans>Select your desired live summary provider</Trans>
      </Text>
      <Select
        id="summary-provider"
        onChange={(e) => onProviderChange(e.target.value)}
        value={copilotConfig.provider}
      >
        <option value="AzureOpenAI">Azure OpenAI</option>
        <option value="OpenAI">OpenAI</option>
      </Select>
      <Text color="greys.400">
        <Trans>Enable the desired modules</Trans>
      </Text>
      {Object.entries(copilotConfig.modules).map(([key, value]) => (
        <HStack key={key}>
          <Switch
            isChecked={value}
            onChange={(e) => onModuleChange(key, e.target.checked)}
          />
          <FormLabel>
            {modulePropWithTitle(copilotConfig.modules)[key].title}
          </FormLabel>
        </HStack>
      ))}
      {copilotConfig && (
        <Button
          primary
          alignSelf="flex-start"
          onClick={() =>
            openTokenModal({ organizationUid: copilotConfig.organizationUid })
          }
        >
          <Trans>Add token</Trans>
        </Button>
      )}
      {copilotConfig?.tokens.map((token) => (
        <Box my="2" key={token.uid}>
          <Text display="inline-block">{token.title}</Text>
          <IconButton
            aria-label="Delete token"
            variant="ghost"
            icon={<MdDelete />}
            onClick={() => {
              openDeleteTokenModal({
                tokenUid: token.uid,
                organizationUid: copilotConfig.organizationUid,
              })
            }}
          />
          <Text>
            {i18n.date(token.created, {
              dateStyle: 'long',
              timeStyle: 'short',
            })}
          </Text>
        </Box>
      ))}
    </VStack>
  )
}
